import React, { useRef, forwardRef, useState, useCallback } from 'react';
import { Pressable, Animated, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { selectionChanged } from '@/constants/utils/haptics';

const supportsHover = Platform.OS === 'macos' || Platform.OS === 'web' || Platform.OS === 'windows';

const Presser = forwardRef(({
  children,
  style,
  onPressIn: customOnPressIn,
  onPressOut: customOnPressOut,
  onHoverIn: customOnHoverIn,
  onHoverOut: customOnHoverOut,
  onPress,
  disabled,
  // Animation config
  scaleAmount = 0.99,    // Extremely subtle scale change
  tension = 25,          // Very gentle movement
  friction = 15,         // Maximum smoothness, zero bounce
  // On iOS/Android, use onPressIn to fire immediately (e.g., for send buttons near keyboard)
  useImmediatePress = false,
  // Rest of pressable props
  ...props
}, ref) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const buttonRef = useRef(null);
  const setButtonRef = useCallback(value => {
    buttonRef.current = value;
    if (typeof ref === 'function') ref(value);
    else if (ref) ref.current = value;
  }, [ref]);
  const [hovered, setHovered] = useState(false);

  const handlePress = (...args) => {
    if (disabled) return;
    // AppKit otherwise leaves mouse clicks focused on the old text field or
    // window, so a dialog cannot return keyboard focus to its launch button.
    if (Platform.OS === 'macos') buttonRef.current?.focus?.();
    onPress?.(...args);
  };

  const animatePress = (pressed) => {
    Animated.spring(scaleAnim, {
      toValue: pressed ? scaleAmount : 1,
      tension,
      friction,
      useNativeDriver: true,
    }).start();
  };

  const handlePressIn = (...args) => {
    animatePress(true);
    selectionChanged();
    customOnPressIn?.(...args);
    // On native platforms with useImmediatePress, fire onPress immediately on touch
    if (useImmediatePress && Platform.OS !== 'web' && onPress) {
      handlePress(...args);
    }
  };

  const handlePressOut = (...args) => {
    animatePress(false);
    customOnPressOut?.(...args);
  };

  const handleHoverIn = useCallback((e) => {
    setHovered(true);
    customOnHoverIn?.(e);
  }, [customOnHoverIn]);

  const handleHoverOut = useCallback((e) => {
    setHovered(false);
    customOnHoverOut?.(e);
  }, [customOnHoverOut]);

  // Determine which press handler to use
  // On web or without useImmediatePress, use normal onPress behavior
  const pressHandler = (useImmediatePress && Platform.OS !== 'web') ? {} : { onPress: handlePress };

  const mergedState = useCallback((state) => ({
    ...state,
    hovered: state.hovered !== undefined ? state.hovered : hovered,
  }), [hovered]);

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <Pressable
        ref={setButtonRef}
        style={typeof style === 'function'
          ? (state) => style(mergedState(state))
          : style
        }
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onHoverIn={supportsHover ? handleHoverIn : undefined}
        onHoverOut={supportsHover ? handleHoverOut : undefined}
        disabled={disabled}
        {...pressHandler}
        {...props}
      >
        {typeof children === 'function' 
          ? (state) => children(mergedState(state))
          : children
        }
      </Pressable>
    </Animated.View>
  );
});

Presser.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  onPressIn: PropTypes.func,
  onPressOut: PropTypes.func,
  onHoverIn: PropTypes.func,
  onHoverOut: PropTypes.func,
  onPress: PropTypes.func,
  disabled: PropTypes.bool,
  scaleAmount: PropTypes.number,
  tension: PropTypes.number,
  friction: PropTypes.number,
  useImmediatePress: PropTypes.bool,
};

Presser.displayName = 'Presser';

export default React.memo(Presser);
